/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * Unsaved-data check registry (#247).
 *
 * `auth.ts` must warn on logout when the vault (or any future service)
 * holds unsaved changes — but `auth.ts` cannot import `vault.ts`
 * directly: `vault.ts` → `tdfetch.ts` → `auth.ts` would close an import
 * cycle. This tiny registry breaks the cycle: producers (vault) register
 * a predicate, the consumer (auth's `logout`) asks the registry.
 */

/** Predicate reporting whether a service currently holds unsaved data. */
export type UnsavedDataCheck = () => boolean;

const checks = new Set<UnsavedDataCheck>();

/**
 * Register a predicate consulted by `hasUnsavedData()`. Returns an
 * unregister function (used by tests; production registrations live for
 * the page lifetime).
 */
export const registerUnsavedDataCheck = (
  check: UnsavedDataCheck
): (() => void) => {
  checks.add(check);
  return () => {
    checks.delete(check);
  };
};

/**
 * True when any registered check reports unsaved data. A throwing check
 * is treated as "no unsaved data" — logout must never be blocked by a
 * broken predicate.
 */
export const hasUnsavedData = (): boolean =>
  [...checks].some(check => {
    try {
      return check();
    } catch {
      return false;
    }
  });
